import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateSubmissionQueries } from "@/lib/mutationHelpers";

export const useCleaningSubmissions = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.submissions.byProperty(propertyId || ''),
    queryFn: async () => {
      if (!propertyId) return [];

      const { data: rows, error } = await supabase
        .from("cleaning_submissions")
        .select("*")
        .eq("property_id", propertyId)
        .order("submitted_at", { ascending: false });

      if (error) throw error;
      if (!rows?.length) return [];

      const cleanerIds = [...new Set(rows.map((r: { cleaner_id?: string }) => r.cleaner_id).filter(Boolean))] as string[];
      const taskIds = [...new Set(rows.map((r: { cleaning_task_id?: string }) => r.cleaning_task_id).filter(Boolean))] as string[];

      const [cleanersRes, tasksRes] = await Promise.all([
        cleanerIds.length ? supabase.from("cleaners").select("id, name").in("id", cleanerIds) : { data: [] },
        taskIds.length
          ? supabase
              .from("cleaning_tasks")
              .select(
                "id, cleaner_id, checklist_id, checklist_completed, inventory_completed, rating, rating_note, rated_at, extra_dirty, extra_dirty_notes, closeout_keys_confirmed, closeout_notes, closeout_keys_photo_url, closeout_state, completed_at, duration_minutes, is_test, test_setup, photos_pending, arrival_tasks, started_at, start_location_lat, start_location_lng, start_location_accuracy_m, start_location_distance_m, start_location_verified, start_location_override_reason, start_location_captured_at, finish_location_lat, finish_location_lng, finish_location_accuracy_m, finish_location_distance_m, finish_location_verified, finish_location_override_reason, finish_location_captured_at, entry_walkthrough_completed, entry_walkthrough_state, guest_setup_completed, guest_setup_state, inventory_guest_phase_completed, inventory_supply_phase_completed",
              )
              .in("id", taskIds)
          : { data: [] },
      ]);

      const cleanerMap = new Map((cleanersRes.data ?? []).map((c: { id: string; name: string }) => [c.id, c]));
      const taskMap = new Map((tasksRes.data ?? []).map((t: { id: string }) => [t.id, t]));

      return rows.map((r: Record<string, unknown>) => ({
        ...r,
        cleaners: r.cleaner_id ? cleanerMap.get(r.cleaner_id as string) ?? null : null,
        cleaning_tasks: r.cleaning_task_id ? taskMap.get(r.cleaning_task_id as string) ?? null : null,
      }));
    },
    enabled: !!propertyId,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
    refetchOnMount: true, // Refetch when Reports tab mounts so new submissions appear
    placeholderData: [], // Show empty array while loading to avoid loading state
  });
};

/** Latest submission for a given cleaning task (for restoring progress when reopening task, e.g. owner/team). */
export const useLatestSubmissionForTask = (cleaningTaskId: string | undefined) => {
  return useQuery({
    queryKey: ["cleaning-submission-for-task", cleaningTaskId],
    queryFn: async () => {
      if (!cleaningTaskId) return null;
      const { data, error } = await supabase
        .from("cleaning_submissions")
        .select("id, tasks_status, completion_percentage, submitted_at")
        .eq("cleaning_task_id", cleaningTaskId)
        .order("submitted_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as { id: string; tasks_status: unknown; completion_percentage: number | null; submitted_at: string } | null;
    },
    enabled: !!cleaningTaskId,
    staleTime: 60_000,
  });
};

export const useRateCleaningTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      rating,
      ratingNote,
    }: {
      taskId: string;
      rating: number;
      ratingNote?: string;
    }) => {
      const { data, error } = await supabase
        .from("cleaning_tasks")
        .update({
          rating,
          rating_note: ratingNote || null,
          rated_at: new Date().toISOString(),
        })
        .eq("id", taskId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateSubmissionQueries(queryClient);
    },
  });
};
